import { useEffect, useState } from "react";

import { useToast } from "@vygruppen/spor-react";

import type { MarkAsFavoriteVariables } from "~/features/favorites/utils.server";
import { QueryBroadcastChannel } from "~/features/query-cache/broadcast-channel";
import {
  circularKeys,
  DEFAULT_QUERY_OPTIONS,
  favoriteKeys,
  linkKeys,
} from "~/features/query-cache/queries";
import { metaSchema } from "~/features/query-cache/utils";
import type { AcknowledgeCircularVariables } from "~/routes/api.order-control";
import { postJson } from "~/utils/fetch-json";

import { datadogRum } from "@datadog/browser-rum";
import type { Query } from "@tanstack/query-core";
import { QueryClient } from "@tanstack/query-core";
import { QueryCache } from "@tanstack/react-query";

export const useInitialQueryClient = () => {
  const toast = useToast();

  const acknowledgeCircular = (body: AcknowledgeCircularVariables) =>
    postJson("/api/order-control", body, { timeoutMs: 15_000 });
  const markAsFavorite = (body: MarkAsFavoriteVariables) =>
    postJson("/api/favorites", body, { timeoutMs: 15_000 });

  // So that we don't make a new QueryClient for every render
  const [queryClient] = useState(() => {
    const client = new QueryClient({
      defaultOptions: DEFAULT_QUERY_OPTIONS,
      queryCache: new QueryCache({
        onError(error, query) {
          handleToast("error", query.meta);
          datadogRum.addError(error);
        },
        onSuccess(_, query) {
          handleToast("success", query.meta);
        },
      }),
    });

    client.setMutationDefaults(circularKeys.acknowledge(), {
      mutationFn: async (variables: AcknowledgeCircularVariables) => {
        // to avoid clashes with optimistic update when an offline mutation continues
        await client.cancelQueries({ queryKey: circularKeys.all() });
        return acknowledgeCircular(variables);
      },
      onSettled: () => {
        client.invalidateQueries({ queryKey: circularKeys.all() });
      },
    });

    client.setMutationDefaults(favoriteKeys.markAsFavorite(), {
      mutationFn: async (variables: MarkAsFavoriteVariables) => {
        if (variables.collection === "circulars") {
          await client.cancelQueries({ queryKey: circularKeys.all() });
        } else if (variables.collection === "links") {
          await client.cancelQueries({ queryKey: linkKeys.all() });
        }
        return markAsFavorite(variables);
      },
      onSettled: (_, __, variables) => {
        if (variables.collection === "circulars") {
          client.invalidateQueries({ queryKey: circularKeys.all() });
        } else if (variables.collection === "links") {
          client.invalidateQueries({ queryKey: linkKeys.all() });
        }
      },
    });

    return client;

    function handleToast(variant: "success" | "error", rawMeta: Query["meta"]) {
      const meta = metaSchema.safeParse(rawMeta);
      if (meta.success && meta.data?.toasts?.[variant]) {
        const text = meta.data?.toasts?.[variant];
        if (text) {
          toast({ variant, text });
        }
      }
    }
  });

  useEffect(
    () =>
      QueryBroadcastChannel.listen((message) => {
        queryClient.setQueryData(message.queryKey, message.data);
      }),
    [queryClient],
  );

  return queryClient;
};
